import { toFiniteNumber } from 'utils/math/math.utils';
import { getValidMapPosition } from 'utils/maps/coordinate.utils';
import { calculateDistanceInMiles } from 'utils/maps/geometry.utils';

const measurementNumberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});

/**
 * Returns a map-ready latitude and longitude for a current condition.
 *
 * @param {object|null} condition - Airport current condition response item.
 * @returns {[number, number]|null} Valid WGS84 coordinates, or null.
 */
export const getCurrentConditionPosition = (condition) =>
  getValidMapPosition(condition?.latitude, condition?.longitude);

/**
 * Returns the numeric temperature for a current condition.
 *
 * @param {object|null} condition - Airport current condition response item.
 * @returns {number|null} Temperature in degrees Fahrenheit, or null.
 */
export const getCurrentConditionTemperature = (condition) => toFiniteNumber(condition?.temperature);

/**
 * Formats a Fahrenheit temperature without implying more than one decimal place of precision.
 *
 * @param {unknown} temperature - Temperature value to format.
 * @returns {string|null} Formatted numeric value without a unit, or null.
 */
export const formatCurrentConditionTemperature = (temperature) => {
  const numericTemperature = toFiniteNumber(temperature);
  return numericTemperature === null ? null : measurementNumberFormatter.format(numericTemperature);
};

/**
 * Formats a numeric current-condition measurement with its unit.
 *
 * Invalid values remain explicitly unavailable instead of producing labels such as
 * "undefined%" or inventing a zero measurement.
 *
 * @param {unknown} value - Measurement value to format.
 * @param {string} unit - Unit appended to a valid numeric measurement.
 * @returns {string} Formatted measurement or an unavailable label.
 */
export const formatCurrentConditionMeasurement = (value, unit) => {
  const numericValue = toFiniteNumber(value);

  return numericValue === null
    ? 'Unavailable'
    : `${measurementNumberFormatter.format(numericValue)}${unit}`;
};

/**
 * Removes current-condition records that cannot be displayed on a map.
 */
export const getMapReadyCurrentConditions = (conditions = []) =>
  (Array.isArray(conditions) ? conditions : []).filter(getCurrentConditionPosition);

/**
 * Parses a backend observation time into an epoch timestamp.
 *
 * Backend utc_time values are UTC but lack a zone designator ('YYYY-MM-DD HH:MM:SS');
 * Date.parse of that form is engine-dependent (NaN in WebKit, local time in V8), so it
 * is normalized to ISO-8601 UTC before parsing.
 *
 * @param {unknown} observationTime - Raw observation time value.
 * @returns {number|null} Epoch milliseconds, or null when unparseable.
 */
export const parseCurrentConditionObservationTime = (observationTime) => {
  if (typeof observationTime !== 'string' || !observationTime.trim()) {
    return null;
  }

  const trimmed = observationTime.trim();
  const isoCandidate = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(trimmed)
    ? `${trimmed.replace(' ', 'T')}Z`
    : trimmed;
  const timestamp = Date.parse(isoCandidate);

  return Number.isFinite(timestamp) ? timestamp : null;
};

/**
 * Returns the most recent valid observation time in the current conditions dataset.
 *
 * @param {object[]} conditions - Airport current condition response items.
 * @returns {Date|null} Most recent observation time, or null.
 */
export const getLatestCurrentConditionObservationTime = (conditions = []) => {
  const latestTimestamp = conditions.reduce((latest, condition) => {
    const timestamp = parseCurrentConditionObservationTime(condition?.utc_time);

    if (timestamp === null || (latest !== null && timestamp <= latest)) {
      return latest;
    }

    return timestamp;
  }, null);

  return latestTimestamp === null ? null : new Date(latestTimestamp);
};

/**
 * Finds the reporting station closest to a map center using great-circle distance.
 *
 * Invalid station and target coordinates are ignored so they cannot become the selected
 * station simply because JavaScript coordinate math produced NaN.
 *
 * @param {object[]} conditions - Airport current condition response items.
 * @param {[number, number]|null} center - Target latitude and longitude.
 * @returns {object|null} Closest valid current condition, or null.
 */
export const getClosestCurrentCondition = (conditions = [], center) => {
  const targetPosition = getCurrentConditionPosition({
    latitude: center?.[0],
    longitude: center?.[1],
  });

  if (!targetPosition || !Array.isArray(conditions)) {
    return null;
  }

  const closestResult = conditions.reduce(
    (closest, condition) => {
      const position = getCurrentConditionPosition(condition);

      if (!position) {
        return closest;
      }

      const distance = calculateDistanceInMiles(
        targetPosition[0],
        targetPosition[1],
        position[0],
        position[1],
      );

      return distance < closest.distance ? { condition, distance } : closest;
    },
    { condition: null, distance: Number.POSITIVE_INFINITY },
  );

  return closestResult.condition;
};

/**
 * Generates a stable key for a current condition marker.
 *
 * @param {object|null} condition - Airport current condition response item.
 * @param {[number, number]|null} [position] - Precomputed result of
 *   getCurrentConditionPosition, to avoid re-parsing the coordinates.
 * @returns {string|null} Stable provider or location key, or null.
 */
export const getCurrentConditionKey = (
  condition,
  position = getCurrentConditionPosition(condition),
) => {
  if (!position) {
    return null;
  }

  const providerId =
    condition?.airport_code ?? condition?.iata_code ?? condition?.city_id ?? condition?.id;

  if (providerId !== null && providerId !== undefined && String(providerId).trim()) {
    return `current-condition-${String(providerId).trim()}`;
  }

  const cityName = String(condition?.city_name || 'unknown-location').trim();

  return `${cityName}-${position[0]}-${position[1]}`;
};

/**
 * Calculates summary statistics from the valid temperature values in an array.
 *
 * Invalid or missing values are excluded from both the mean and the observation count.
 *
 * @param {unknown[]} temperatures - Temperature observations to summarize.
 * @returns {{meanTemperature: number|null, observationCount: number}} Temperature statistics.
 */
export const getTemperatureStatistics = (temperatures = []) => {
  const validTemperatures = temperatures
    .map((temperature) => toFiniteNumber(temperature))
    .filter((temperature) => temperature !== null);

  if (!validTemperatures.length) {
    return {
      meanTemperature: null,
      observationCount: 0,
    };
  }

  const totalTemperature = validTemperatures.reduce(
    (previousTotal, temperature) => previousTotal + temperature,
    0,
  );

  return {
    meanTemperature: totalTemperature / validTemperatures.length,
    observationCount: validTemperatures.length,
  };
};
